/**
 * Dashboard Overview Page
 * Module 07 - Dashboard UI
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Code, Upload } from 'lucide-react';
import { useAuth } from '../hooks';
import { useModels } from '../hooks/useModels';
import { useEmbeds } from '../hooks/useEmbeds';
import { formatBytes, formatPercentage } from '../utils';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { models, storageUsage } = useModels();
  const { embeds } = useEmbeds();

  const storageUsed = models.reduce((acc, m) => acc + (m.originalFileSize || 0), 0);
  const storageQuota = storageUsage?.quota ?? user?.storageQuota ?? 0;
  const storagePercentage = storageQuota > 0 ? (storageUsed / storageQuota) * 100 : 0;

  const activeEmbedsCount = embeds.filter((e) => e.isActive).length;
  const embedQuota = user?.embedQuota ?? 0;
  const embedPercentage = embedQuota > 0 ? (activeEmbedsCount / embedQuota) * 100 : 0;

  const colorClasses = {
    blue: {
      badge: 'bg-blue-100',
      icon: 'text-blue-600',
      bar: 'bg-blue-600',
    },
    green: {
      badge: 'bg-green-100',
      icon: 'text-green-600',
      bar: 'bg-green-600',
    },
    purple: {
      badge: 'bg-purple-100',
      icon: 'text-purple-600',
      bar: 'bg-purple-600',
    },
  } as const;

  const stats = [
    {
      name: 'Storage Used',
      value: formatBytes(storageUsed),
      total: formatBytes(storageQuota),
      percentage: storagePercentage,
      icon: Upload,
      colour: 'blue' as const,
    },
    {
      name: 'Active Embeds',
      value: activeEmbedsCount.toString(),
      total: embedQuota.toString(),
      percentage: embedPercentage,
      icon: Code,
      colour: 'green' as const,
    },
  ];

  const quickActions: Array<{
    name: string;
    description: string;
    icon: typeof Upload;
    href: string;
    colour: string;
    state?: { openUpload: boolean };
  }> = [
    {
      name: 'Upload Model',
      description: 'Upload a new 3D model file',
      icon: Upload,
      href: '/models',
      colour: 'bg-blue-500',
      state: { openUpload: true },
    },
    {
      name: 'Create Embed',
      description: 'Generate embed code for your models',
      icon: Code,
      href: '/embeds',
      colour: 'bg-green-500',
    },
    {
      name: 'View Models',
      description: 'Browse your 3D model library',
      icon: Box,
      href: '/models',
      colour: 'bg-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Here's an overview of your 3D Fashion Visualization Platform
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {stat.value}
                  <span className="text-sm text-gray-500 ml-1">/ {stat.total}</span>
                </p>
              </div>
              <div className={`rounded-full p-3 ${colorClasses[stat.colour].badge}`}>
                <stat.icon className={`h-6 w-6 ${colorClasses[stat.colour].icon}`} />
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">Usage</span>
                <span className="font-medium text-gray-900">
                  {formatPercentage(stat.percentage / 100, 1)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`${colorClasses[stat.colour].bar} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              state={action.state}
              className="card hover:shadow-md transition-shadow duration-200 group"
            >
              <div className="flex items-start gap-4">
                <div className={`${action.colour} rounded-lg p-3 group-hover:scale-110 transition-transform duration-200`}>
                  <action.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {action.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {action.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <p className="text-sm text-gray-500">
          Activity tracking coming soon...
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
